import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import {cva, type VariantProps} from "class-variance-authority"

import {cn} from "@/shared/utils/utils.ts"

const TooltipProvider = TooltipPrimitive.Provider
const Tooltip = TooltipPrimitive.Root
const TooltipTrigger = TooltipPrimitive.Trigger

const tooltipVariants = cva(
  "z-50 overflow-hidden rounded-apple-md border shadow-apple-md animate-in data-[state=closed]:animate-out data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  {
    variants: {
      variant: {
        default: "bg-popover text-popover-foreground border-input",
        inverted: "bg-foreground text-background border-foreground/20",
        brand: "bg-primary text-primary-foreground border-primary/60",
        muted: "bg-muted text-foreground border-transparent",
      },
      size: {
        sm: "px-2 py-1 text-xs",
        md: "px-3 py-1.5 text-sm",
        lg: "px-3.5 py-2 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
)

interface TooltipContentProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
  VariantProps<typeof tooltipVariants> {
  showArrow?: boolean
}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 6, variant, size, showArrow = true, ...props }, ref) => {
  const arrowFill =
    variant === "inverted"
      ? "fill-[hsl(var(--foreground))]"
      : variant === "brand"
        ? "fill-[hsl(var(--primary))]"
        : variant === "muted"
          ? "fill-[hsl(var(--muted))]"
          : "fill-[hsl(var(--popover))]"

  return (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(tooltipVariants({ variant, size }), className)}
      {...props}
    >
      {props.children}
      {showArrow && (
        <TooltipPrimitive.Arrow className={cn("h-2 w-3", arrowFill)} />
      )}
    </TooltipPrimitive.Content>
  )
})
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
